import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ContentPillar, ContentPlan } from '../types';

type CreationMode = 'quick' | 'specific' | 'manual';

/** Hours threshold below which we warn the user tasks will be due immediately. */
const IMMEDIATE_THRESHOLD_HOURS = 3;

function isImmediateSchedule(scheduledDate?: Date | null): boolean {
  if (!scheduledDate) return false;
  const diffMs = scheduledDate.getTime() - Date.now();
  return diffMs <= IMMEDIATE_THRESHOLD_HOURS * 60 * 60 * 1000;
}

type OutlineRequestCard = {
  pillar: number | null;
  content_format: string;
  forced_date: string;
};

const FORMAT_LABELS: Record<string, string> = {
  image: 'Image',
  carousel: 'Carousel',
  video: 'Video',
};

const DEFAULT_OUTLINE_CARD = (defaultFormat: string): OutlineRequestCard => ({
  pillar: null,
  content_format: defaultFormat,
  forced_date: '',
});

function formatDateForPrompt(dateString: string): string {
  const date = new Date(`${dateString}T12:00:00`);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function dateToPublishIso(dateString: string): string {
  return new Date(`${dateString}T09:00:00`).toISOString();
}

/** Today's local date as YYYY-MM-DD, for use as a date input `min` / past-date guard. */
function todayLocalDateString(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** True when a YYYY-MM-DD date string is before today (lexical compare is safe for ISO dates). */
function isPastDate(dateString: string): boolean {
  if (!dateString) return false;
  return dateString < todayLocalDateString();
}

type OutlineCreationModalProps = {
  pillars: ContentPillar[];
  plans: ContentPlan[];
  allowedFormats: string[];
  onClose: () => void;
  onCreateOwn: (_data: {
    title: string;
    summary: string;
    content_format: string;
    pillar: number | null;
    plan: number | null;
    publish_at: string | null;
  }) => Promise<void>;
  onCreateWithAI: (_data: {
    plan: number | null;
    notes: string;
    outlines: Array<{
      pillar: number | null;
      content_format: string;
      forced_publish_at: string | null;
      force_date_instruction: string | null;
    }>;
  }) => Promise<void>;
  loading?: boolean;
  initialMode?: CreationMode;
  initialNotes?: string;
  /** Whether workspace auto-approve is enabled. Shows a reminder banner. */
  autoApprove?: boolean;
  /** Target publish date — if within 3 h, shows an "immediate tasks" hint. */
  scheduledDate?: Date | null;
  initialForcedDate?: string;
};

export function OutlineCreationModal({
  pillars,
  plans,
  allowedFormats,
  onClose,
  onCreateOwn,
  onCreateWithAI,
  loading = false,
  initialMode = 'quick',
  initialNotes,
  autoApprove,
  scheduledDate,
  initialForcedDate,
}: OutlineCreationModalProps) {
  const [mode, setMode] = useState<CreationMode>(initialMode);
  const [error, setError] = useState('');

  const formatOptions = useMemo(() => {
    const fallback = ['image', 'carousel', 'video'];
    const base = allowedFormats.length > 0 ? allowedFormats : fallback;
    return base.map((value) => ({
      value,
      label: FORMAT_LABELS[value] ?? value.charAt(0).toUpperCase() + value.slice(1),
    }));
  }, [allowedFormats]);

  const goQuick = () => {
    setMode('quick');
    setError('');
  };

  const immediate = isImmediateSchedule(scheduledDate);

  /** Banners rendered inside every modal screen */
  const hints = (
    <>
      {autoApprove !== undefined && (
        <div
          style={{
            margin: '0 0 12px 0',
            padding: '10px 14px',
            borderRadius: 8,
            fontSize: 13,
            background: autoApprove
              ? 'var(--color-success-bg, #e6f9f0)'
              : 'var(--color-warning-bg, #fff8e1)',
            color: autoApprove
              ? 'var(--color-success-text, #1a7a4a)'
              : 'var(--color-warning-text, #7a5a00)',
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
          }}
        >
          <span>{autoApprove ? '✅' : '⚠️'}</span>
          <span>
            {autoApprove
              ? 'Auto-approve is ON — tasks will run automatically without your review.'
              : "Auto-approve is OFF — you'll need to approve each step before tasks run."}
          </span>
        </div>
      )}
      {immediate && (
        <div
          style={{
            margin: '0 0 12px 0',
            padding: '10px 14px',
            borderRadius: 8,
            fontSize: 13,
            background: 'var(--color-info-bg, #e8f4fd)',
            color: 'var(--color-info-text, #0a558c)',
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
          }}
        >
          <span>⚡</span>
          <span>
            This post is scheduled soon — prep tasks will be due <strong>immediately</strong> once
            the outline is saved.
          </span>
        </div>
      )}
    </>
  );

  if (mode === 'manual') {
    return (
      <CreateYourOwnForm
        pillars={pillars}
        plans={plans}
        formatOptions={formatOptions}
        onClose={onClose}
        onBack={goQuick}
        onSubmit={onCreateOwn}
        loading={loading}
        error={error}
        setError={setError}
        hints={hints}
        initialForcedDate={initialForcedDate}
      />
    );
  }

  if (mode === 'specific') {
    return (
      <CreateWithAIForm
        pillars={pillars}
        plans={plans}
        formatOptions={formatOptions}
        onClose={onClose}
        onBack={goQuick}
        onSubmit={onCreateWithAI}
        loading={loading}
        error={error}
        setError={setError}
        initialNotes={initialNotes}
        hints={hints}
        initialForcedDate={initialForcedDate}
      />
    );
  }

  // mode === 'quick' — the default: one freeform box, agent does the rest.
  return (
    <QuickAIForm
      onClose={onClose}
      onSubmit={onCreateWithAI}
      onBuildSpecific={() => {
        setError('');
        setMode('specific');
      }}
      onCreateManual={() => {
        setError('');
        setMode('manual');
      }}
      loading={loading}
      error={error}
      setError={setError}
      initialNotes={initialNotes}
      hints={hints}
    />
  );
}

type QuickAIFormProps = {
  onClose: () => void;
  onSubmit: (_data: {
    plan: number | null;
    notes: string;
    outlines: Array<{
      pillar: number | null;
      content_format: string;
      forced_publish_at: string | null;
      force_date_instruction: string | null;
    }>;
  }) => Promise<void>;
  onBuildSpecific: () => void;
  onCreateManual: () => void;
  loading: boolean;
  error: string;
  setError: (_error: string) => void;
  initialNotes?: string;
  hints?: React.ReactNode;
};

function QuickAIForm({
  onClose,
  onSubmit,
  onBuildSpecific,
  onCreateManual,
  loading,
  error,
  setError,
  initialNotes,
  hints,
}: QuickAIFormProps) {
  const [notes, setNotes] = useState(initialNotes ?? '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!notes.trim()) {
      setError('Describe what you want to post about.');
      return;
    }
    try {
      setError('');
      // Quick mode sends no per-post specs — the agent decides count and dates.
      await onSubmit({ plan: null, notes: notes.trim(), outlines: [] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create outlines';
      setError(message);
    }
  };

  return createPortal(
    <div
      className="modal-overlay"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create posts</h2>
          <button className="modal-close" onClick={onClose} disabled={loading}>
            x
          </button>
        </div>
        {error && (
          <div
            style={{
              padding: 12,
              margin: '0 20px 16px',
              background: '#fee',
              color: '#c00',
              borderRadius: 8,
            }}
          >
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {hints}
            <div className="form-group">
              <label>What should we create?</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={
                  'e.g. 5 new posts about autumn foliage, scheduled after the latest ones'
                }
                rows={4}
                autoFocus
                disabled={loading}
              />
              <p style={{ marginTop: 8, color: 'var(--text-secondary)', fontSize: 13 }}>
                Describe the topic, how many, and any timing. The AI picks the formats and
                publish dates and adds them to your queue.
              </p>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 16,
                marginTop: 4,
                fontSize: 13,
              }}
            >
              <button
                type="button"
                className="link-button"
                onClick={onBuildSpecific}
                disabled={loading}
                style={{ background: 'none', border: 'none', color: 'var(--color-primary-600, #2563eb)', cursor: 'pointer', padding: 0 }}
              >
                Build specific posts →
              </button>
              <button
                type="button"
                className="link-button"
                onClick={onCreateManual}
                disabled={loading}
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 0 }}
              >
                Create one manually →
              </button>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create with AI'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

type CreateYourOwnFormProps = {
  pillars: ContentPillar[];
  plans: ContentPlan[];
  formatOptions: Array<{ value: string; label: string }>;
  onClose: () => void;
  onBack: () => void;
  onSubmit: (_data: {
    title: string;
    summary: string;
    content_format: string;
    pillar: number | null;
    plan: number | null;
    publish_at: string | null;
  }) => Promise<void>;
  loading: boolean;
  error: string;
  setError: (_error: string) => void;
  hints?: React.ReactNode;
  initialForcedDate?: string;
};

function CreateYourOwnForm({
  pillars,
  plans,
  formatOptions,
  onClose,
  onBack,
  onSubmit,
  loading,
  error,
  setError,
  hints,
  initialForcedDate,
}: CreateYourOwnFormProps) {
  const [form, setForm] = useState({
    title: '',
    summary: '',
    content_format: formatOptions[0]?.value ?? 'image',
    pillar: null as number | null,
    plan: null as number | null,
    publish_date: initialForcedDate ?? '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    if (isPastDate(form.publish_date)) {
      setError("Publish date can't be in the past.");
      return;
    }
    try {
      setError('');
      const { publish_date, ...rest } = form;
      await onSubmit({
        ...rest,
        publish_at: publish_date ? dateToPublishIso(publish_date) : null,
      });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create outline';
      setError(message);
    }
  };

  return createPortal(
    <div
      className="modal-overlay"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Outline</h2>
          <button className="modal-close" onClick={onClose} disabled={loading}>
            x
          </button>
        </div>
        {error && (
          <div
            style={{
              padding: 12,
              margin: '0 20px 16px',
              background: '#fee',
              color: '#c00',
              borderRadius: 8,
            }}
          >
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {hints}
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., 5 Tips for Better Workouts"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Summary</label>
              <textarea
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                placeholder="Brief description of the content..."
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Content Format</label>
              <select
                value={form.content_format}
                onChange={(e) => setForm({ ...form, content_format: e.target.value })}
                disabled={loading}
              >
                {formatOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Pillar (optional)</label>
              <select
                value={form.pillar ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    pillar: e.target.value ? Number(e.target.value) : null,
                  })
                }
                disabled={loading}
              >
                <option value="">No pillar</option>
                {pillars.map((pillar) => (
                  <option key={pillar.id} value={pillar.id}>
                    {pillar.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Content Plan (optional)</label>
              <select
                value={form.plan ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    plan: e.target.value ? Number(e.target.value) : null,
                  })
                }
                disabled={loading}
              >
                <option value="">No plan</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Publish date (optional)</label>
              <input
                type="date"
                value={form.publish_date}
                min={todayLocalDateString()}
                onChange={(e) => setForm({ ...form, publish_date: e.target.value })}
                disabled={loading}
              />
              <p style={{ marginTop: 6, color: 'var(--text-secondary)', fontSize: 13 }}>
                Set a date to schedule this post and start its content + publish tasks. Leave
                empty to save it as an unscheduled draft.
              </p>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onBack} disabled={loading}>
              Back
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

type CreateWithAIFormProps = {
  pillars: ContentPillar[];
  plans: ContentPlan[];
  formatOptions: Array<{ value: string; label: string }>;
  onClose: () => void;
  onBack: () => void;
  onSubmit: (_data: {
    plan: number | null;
    notes: string;
    outlines: Array<{
      pillar: number | null;
      content_format: string;
      forced_publish_at: string | null;
      force_date_instruction: string | null;
    }>;
  }) => Promise<void>;
  loading: boolean;
  error: string;
  setError: (_error: string) => void;
  initialNotes?: string;
  hints?: React.ReactNode;
  initialForcedDate?: string;
};

function CreateWithAIForm({
  pillars,
  plans,
  formatOptions,
  onClose,
  onBack,
  onSubmit,
  loading,
  error,
  setError,
  initialNotes,
  hints,
  initialForcedDate,
}: CreateWithAIFormProps) {
  const [plan, setPlan] = useState<number | null>(null);
  const [notes, setNotes] = useState(initialNotes ?? '');
  const [outlineCards, setOutlineCards] = useState<OutlineRequestCard[]>([
    {
      ...DEFAULT_OUTLINE_CARD(formatOptions[0]?.value ?? 'image'),
      forced_date: initialForcedDate ?? '',
    },
  ]);

  const canAdd = outlineCards.length < 30;

  const updateCard = (index: number, patch: Partial<OutlineRequestCard>) => {
    setOutlineCards((prev) => prev.map((card, i) => (i === index ? { ...card, ...patch } : card)));
  };

  const addCard = () => {
    if (!canAdd) return;
    setOutlineCards((prev) => [...prev, DEFAULT_OUTLINE_CARD(formatOptions[0]?.value ?? 'image')]);
  };

  const removeCard = (index: number) => {
    if (index === 0) return;
    setOutlineCards((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (outlineCards.length < 1) {
      setError('Create at least one outline card.');
      return;
    }
    if (outlineCards.some((card) => isPastDate(card.forced_date))) {
      setError("Forced post dates can't be in the past.");
      return;
    }
    try {
      setError('');
      await onSubmit({
        plan,
        notes,
        outlines: outlineCards.map((card) => {
          const forcedPublishAt = card.forced_date ? dateToPublishIso(card.forced_date) : null;
          const forceDateInstruction = card.forced_date
            ? `Force this post for ${formatDateForPrompt(card.forced_date)}.`
            : null;
          return {
            pillar: card.pillar,
            content_format: card.content_format,
            forced_publish_at: forcedPublishAt,
            force_date_instruction: forceDateInstruction,
          };
        }),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create outlines';
      setError(message);
    }
  };

  return createPortal(
    <div
      className="modal-overlay"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>AI Outline Builder</h2>
          <button className="modal-close" onClick={onClose} disabled={loading}>
            x
          </button>
        </div>
        {error && (
          <div
            style={{
              padding: 12,
              margin: '0 20px 16px',
              background: '#fee',
              color: '#c00',
              borderRadius: 8,
            }}
          >
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {loading && (
              <div className="outline-ai-generating" role="status" aria-live="polite">
                <div className="outline-ai-generating__title">Generating your outlines…</div>
                <div className="outline-ai-generating__subtitle">
                  Conistra Brain is building ideas for your account.
                </div>
                <div className="outline-ai-generating__dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="outline-ai-generating__steps" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
            {!loading && hints}
            <div className="form-group">
              <label>Content Plan (optional)</label>
              <select
                value={plan ?? ''}
                onChange={(e) => setPlan(e.target.value ? Number(e.target.value) : null)}
                disabled={loading}
              >
                <option value="">Any plan</option>
                {plans.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Outline Specs</label>
              <div className="outline-builder-grid">
                {outlineCards.map((card, index) => (
                  <div key={index} className="outline-builder-card">
                    <div className="outline-builder-card__head">
                      <div className="outline-builder-card__index">Outline {index + 1}</div>
                      {index > 0 && (
                        <button
                          type="button"
                          className="outline-builder-card__remove"
                          onClick={() => removeCard(index)}
                          disabled={loading}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Format</label>
                      <select
                        value={card.content_format}
                        onChange={(e) => updateCard(index, { content_format: e.target.value })}
                        disabled={loading}
                      >
                        {formatOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Pillar (optional)</label>
                      <select
                        value={card.pillar ?? ''}
                        onChange={(e) =>
                          updateCard(index, {
                            pillar: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                        disabled={loading}
                      >
                        <option value="">Any pillar</option>
                        {pillars.map((pillar) => (
                          <option key={pillar.id} value={pillar.id}>
                            {pillar.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Force Post Date (optional)</label>
                      <input
                        type="date"
                        value={card.forced_date}
                        min={todayLocalDateString()}
                        onChange={(e) => updateCard(index, { forced_date: e.target.value })}
                        disabled={loading}
                      />
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="outline-builder-add"
                  onClick={addCard}
                  disabled={loading || !canAdd}
                  title={canAdd ? 'Add outline spec' : 'Maximum 30 outlines'}
                >
                  <span className="outline-builder-add__plus">+</span>
                  <span className="outline-builder-add__label">Add outline</span>
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>
                Guidance for AI{' '}
                <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Tone, audience angle, topics to include or avoid..."
                disabled={loading}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onBack} disabled={loading}>
              Back
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading
                ? 'Generating…'
                : `Generate ${outlineCards.length} Outline${outlineCards.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
